import {
  isExtension,
  showToast,
  teamHasDialler,
  yesNoConfirm,
} from "@sourcewhale/core";
import { SwApiStore } from "../../../api-stores/sw-api-store";
import { getCall } from "../../../api/calls";
import {
  getVoicemails,
  modifyCandidate,
  participants,
} from "../../../api/dialler";
import { attendeesState } from "./attendeesState.svelte";
import { userStore } from "../../../api-stores/user-store";
import { getCandidate } from "../../../api/candidates";
import { TimerState } from "./timerState.svelte";
import { twilioManager } from "./utils/TwilioManager";
import { getPhone } from "./utils/phoneUtils";

export class DiallerState {
  #beforeUnloadHandler = null;
  #isInitiatingCall = false;
  #voicemailsStore = null;

  timer = null;
  inviteTeammateTimer = null;
  user = null;

  constructor() {
    this.timer = new TimerState();
    userStore.subscribe((user) => {
      this.user = user;
    });
  }

  // ===== Reactive State (UI-bound) =====
  // These properties trigger UI updates when changed
  isOpen = $state(false);
  // 'user' | 'manager' | 'keypad-dial' | 'keypad' | 'outcome' | 'addTeammate' | 'invite'
  viewMode = $state(isExtension() ? "keypad-dial" : "user");
  call = $state(null);

  // UI visibility and display states
  activeCall = $state(null);
  isMuted = $state(false);
  isRecording = $state(false);
  isConferenceCall = $state(false);

  // Device selections (UI dropdowns)
  selectedInputDevice = $state(null);
  selectedOutputDevice = $state(null);
  availableDevices = $state({ inputs: [], outputs: [] });

  // UI warnings and modals
  networkWarning = $state(null);
  audioWarning = $state(null);
  isLogged = $state(false);

  // Keypad state
  keypadInputDigits = $state(""); // Accumulated DTMF digits from keypad

  // Microphone permission state (UI-bound for potential permission warnings)
  microphonePermission = $state("prompt"); // "prompt" | "granted" | "denied"

  // Save contact notification
  showSaveNotification = $state(false);

  // ===== Internal State (non-reactive) =====
  // These properties are for internal logic and don't directly update UI

  // Backend IDs and metadata
  callId = null;
  todoId = null;
  conferenceId = null;
  callDirection = "outbound"; // "inbound" | "outbound"

  // Recording state tracking
  callHasRecording = false; // Track if recording was ever started during the call

  ableToAddTeammate = $state(true);

  get voicemailsStore() {
    if (!this.#voicemailsStore) {
      this.#voicemailsStore = SwApiStore({
        get: () => getVoicemails({ kind: "message", includeUrl: true }),
        init: () => ({ items: [] }),
        cacheKey: "dialler-voicemails",
        cacheDurationMs: 5 * 60 * 1000,
      });
    }
    return this.#voicemailsStore;
  }

  #checkForActiveCall = (params) => {
    const { phoneNumber, autoCall } = params;

    // Check if there's an active call and we're trying to make a new one
    if (this.activeCall && phoneNumber && autoCall) {
      yesNoConfirm(
        "You are currently on a call. Do you want to end the current call?",
        async () => {
          // User confirmed - end current call and start new one
          try {
            if (twilioManager.activeCall) {
              await twilioManager.activeCall.disconnect();
            }
          } catch (error) {
            console.error("Failed to end current call:", error);
          }
          // Reset state and open with new number
          this.reset();
          this.openDialler(params);
          return false;
        },
        // User cancelled the new call, return so the new call is not initiated
        () => true,
      );
    } else {
      return false;
    }
  };

  makeCall(attendees, todoId) {
    attendeesState.attendees = attendees;
    attendeesState.currentUserType = "user";
    this.openDialler({
      todoId: todoId,
    });
  }

  joinConference(conferenceId, attendees, mode) {
    attendeesState.attendees = attendees;

    if (!conferenceId) {
      throw new Error("Conference ID is required");
    }

    twilioManager.joinConference({ conferenceId, mode });

    this.openDialler({
      autoCall: false,
    });

    if (mode == "listen") {
      this.viewMode = "manager";
      attendeesState.currentUserType = "manager";
    } else if (mode == "teammate") {
      this.viewMode = "user";
      attendeesState.currentUserType = "teammate";
    }

    this.isConferenceCall = true;
    this.conferenceId = conferenceId;
  }

  /**
   * Gets the live call from the call ID
   * @param {string} callId - The call ID
   * @returns {Promise<Object>} The live call
   */

  async getLiveCall(callId) {
    try {
      const response = await getCall({ callId });
      return response?.call;
    } catch (error) {
      console.error("Failed to get live call:", error);
      return null;
    }
  }

  /**
   * Handles the user joining a conference call from an invite
   * @param {Object} inviteData - The invite data from the backend with the call info
   */
  async openInvite(inviteData) {
    // Don't open invite if already on a call or using the dialler
    if ((this.isOpen && !isExtension()) || this.activeCall) {
      return;
    }

    const liveCall = await this.getLiveCall(inviteData.callId);

    if (!liveCall) {
      return;
    }

    const candidateId = inviteData.candidateId;
    attendeesState.candidate = candidateId
      ? await getCandidate({ candidateId })
      : {
          name: "Unknown Contact",
          phone: liveCall.to,
        };

    attendeesState.convertCallToAttendees(liveCall);

    if (!attendeesState.candidate || !attendeesState.user) {
      throw new Error("Candidate or user not found in call");
    }

    this.callId = inviteData.callId;
    this.conferenceId = inviteData.conferenceId;
    this.ableToAddTeammate = false;
    this.isOpen = true;
    this.viewMode = "invite";
    this.timer.startDurationTimer();
  }

  /**
   * Handles the user's response to an invite
   * @param {Object} inviteData - The invite data from the backend with the call info
   */
  async inviteResponse(inviteData) {
    // Don't handle invite response if it's not for the current user
    if (attendeesState.user?.userId !== inviteData.invitedUserId) {
      return;
    }

    const liveCall = await this.getLiveCall(inviteData.callId);
    if (!liveCall) {
      return;
    }

    if (inviteData.invite.status === "accepted") {
      this.isConferenceCall = true;
      attendeesState.teammate = {
        ...attendeesState.teammate,
        inviteStatus: "accepted",
      };
    } else if (inviteData.invite.status === "declined") {
      attendeesState.teammate = {
        ...attendeesState.teammate,
        inviteStatus: "declined",
      };
      this.ableToAddTeammate = true;
    }
  }

  /**
   * Opens the dialler
   * @param {Object} params - The parameters for the dialler
   * @param {string} params.todoId - The todo ID
   * @param {boolean} params.autoCall - Whether to auto-call
   */
  async openDialler({ todoId, autoCall = true }) {
    if (!teamHasDialler(true, this.user)) {
      return;
    }

    const candidate = { ...attendeesState.candidate };

    if (
      this.#checkForActiveCall({
        phoneNumber: candidate.phone,
        autoCall,
      })
    ) {
      return;
    }

    this.isOpen = true;
    this.todoId = todoId;

    // beforeunload prevents memory leaks and ensures proper cleanup
    if (!this.#beforeUnloadHandler) {
      this.#beforeUnloadHandler = (e) => {
        if (this.activeCall) {
          const message =
            "It looks like you are on a live call, do you still want to leave?";
          e.preventDefault();
          e.returnValue = message;
          return message;
        }
      };
      window.addEventListener("beforeunload", this.#beforeUnloadHandler);
    }

    // Trigger data fetch
    if (this.voicemailsStore) {
      this.voicemailsStore.get();
    }

    if (candidate.phone && autoCall && !this.#isInitiatingCall) {
      this.#isInitiatingCall = true;

      this.initiateAutoCall({
        phoneNumber: attendeesState.candidate.phone,
        todoId,
        candidateId: attendeesState.candidate.candidateId,
      });
    }
  }

  /**
   * Initiate an auto-call with proper initialization
   * @param {Object} params - Call parameters
   * @private
   */
  async initiateAutoCall({ phoneNumber, todoId, candidateId }) {
    try {
      await twilioManager.makeCall({
        phoneNumber,
        todoId,
        candidateId,
      });
      // Timer will be started by TwilioManager when call is accepted
    } catch (error) {
      console.error("Auto-call failed:", error);
      showToast({
        title: "Unable to start call",
        subtitle: "Please try again manually",
        type: "warning",
      });
    } finally {
      this.#isInitiatingCall = false;
    }
  }

  /**
   * Adds a teammate to the call
   * @param {Object} teammate - The teammate to add
   */
  async addTeammate(teammate) {
    this.isConferenceCall = true;
    attendeesState.teammate = teammate;
    this.ableToAddTeammate = false;
    this.viewMode = "user";

    // TODO: Redirect to phone number if timeout
    this.inviteTeammateTimer = setTimeout(async () => {
      // If the teammate didn't accept the invite within 10 seconds, decline the invite
      if (attendeesState.teammate.inviteStatus !== "accepted") {
        attendeesState.teammate.inviteStatus = "declined";
        this.ableToAddTeammate = true;

        await participants({
          action: "invite",
          callId: this.callId,
          inviteStatus: "declined",
        });
      }
    }, 10000);
  }

  /**
   * Opens the keypad extension view
   */
  openKeypadExtension() {
    this.isOpen = true;
    this.viewMode = "keypad-dial";
  }

  /**
   * Apply the candidate information to the dialler state
   * @param {Object} candidateId - The candidate ID
   */
  async applyCandidateInformation(candidateId) {
    if (this.activeCall) {
      console.log("Already on a call, not applying candidate information");
      return;
    }
    if (candidateId) {
      const candidate = await getCandidate({ candidateId });
      const phone = getPhone(candidate);
      if (candidate) {
        attendeesState.candidate = {
          ...candidate,
          phone,
          role: "candidate",
        };
        return;
      }
    }

    attendeesState.candidate = {
      ...(attendeesState.candidate || {}),
      name: "Unknown Contact",
      role: "candidate",
    };
  }

  acceptInboundCall() {
    this.callDirection = "inbound";
    twilioManager.activeCall.accept();
    this.viewMode = "user";
    attendeesState.currentUserType = "user";
    this.activeCall = twilioManager.activeCall;
  }

  rejectInboundCall() {
    twilioManager.activeCall.reject();
    this.reset();
  }

  closeDialler() {
    // Remove beforeunload handler before ending call
    if (this.#beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }

    if (this.activeCall) {
      this.endCall();
    }

    // Users should see the call button again
    if (attendeesState.currentUserType === "user") {
      this.viewMode = "user";
      this.resetCallState();
    } else {
      this.reset();
    }

    if (!isExtension()) this.isOpen = false;
    this.#voicemailsStore = null;
  }

  startCall() {
    // Ensure we don't have multiple timers running
    this.timer.stopDurationTimer();
    this.callHasRecording = false; // Reset recording state for new call
    this.keypadInputDigits = ""; // Clear keypad input for new call
  }

  /**
   * End the active call
   */
  async endCall() {
    this.timer.stopDurationTimer();
    try {
      if (twilioManager.activeCall) await twilioManager.activeCall.disconnect();
    } catch {
      showToast({
        title: "Dialer Error",
        subtitle: "Unable to end call",
        type: "danger",
      });
    }
  }

  async createCandidate(name, email, phone) {
    try {
      const payload = {
        action: "create",
        name,
        emails: [email],
        phones: [phone],
      };
      await modifyCandidate(payload);
    } catch {
      showToast({
        title: "Error",
        subtitle: "Failed to create candidate",
        type: "error",
      });
    }
  }

  showSaveContactNotification() {
    this.showSaveNotification = true;
    setTimeout(() => {
      this.showSaveNotification = false;
    }, 3000);
  }

  setWarning(type, message) {
    if (type === "network") {
      this.networkWarning = message;
    } else if (type === "audio") {
      this.audioWarning = message;
    }
  }

  clearWarning(type) {
    if (type === "network") {
      this.networkWarning = null;
    } else if (type === "audio") {
      this.audioWarning = null;
    }
  }

  // Device management
  updateAvailableDevices(devices) {
    if (!devices || typeof devices !== "object") {
      return;
    }

    // Ensure proper structure
    const inputs = Array.isArray(devices.inputs) ? devices.inputs : [];
    const outputs = Array.isArray(devices.outputs) ? devices.outputs : [];

    this.availableDevices = { inputs, outputs };

    // Check if current selected devices still exist
    const currentInputExists = inputs.some(
      (d) => d.deviceId === this.selectedInputDevice,
    );
    const currentOutputExists = outputs.some(
      (d) => d.deviceId === this.selectedOutputDevice,
    );

    // Auto-select first device if none selected or current device no longer exists
    if (
      (!this.selectedInputDevice || !currentInputExists) &&
      inputs.length > 0 &&
      inputs[0].deviceId
    ) {
      this.selectedInputDevice = inputs[0].deviceId;
    }
    if (
      (!this.selectedOutputDevice || !currentOutputExists) &&
      outputs.length > 0 &&
      outputs[0].deviceId
    ) {
      this.selectedOutputDevice = outputs[0].deviceId;
    }
  }
  // Reset call specific state to retain last candidate but clear call info
  resetCallState() {
    this.isMuted = false;
    this.isRecording = false;
    this.isConferenceCall = false;
    this.networkWarning = null;
    this.audioWarning = null;
    this.keypadInputDigits = "";
    this.showSaveNotification = false;
    this.isLogged = false;
    twilioManager.activeCall = null;
    this.activeCall = null;
  }

  reset() {
    this.timer.stopDurationTimer();
    attendeesState.reset();

    this.#isInitiatingCall = false;

    this.resetCallState();

    // Reset internal state (non-reactive)
    // Note: activeCall is now managed by TwilioManager
    this.callId = null;
    this.callDirection = "outbound";
    this.todoId = null;
    this.conferenceId = null;
    this.selectedVoicemail = null;
    this.callHasRecording = false;
    this.isLogged = false;

    // Reset teammate/coach state
    this.ableToAddTeammate = true;

    // Reset view mode
    this.viewMode = isExtension() ? "keypad-dial" : "user";
  }

  // Cleanup method
  async destroy() {
    // Stop any running timers first
    this.timer.stopDurationTimer();

    // Clear invite teammate timer
    if (this.inviteTeammateTimer) {
      clearTimeout(this.inviteTeammateTimer);
      this.inviteTeammateTimer = null;
    }

    // Cleanup Twilio resources through manager
    try {
      await twilioManager.cleanup();
    } catch (error) {
      console.error("Error cleaning up Twilio resources:", error);
    }

    // Clean up beforeunload listener (should already be removed by closeDialler)
    if (this.#beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }
    // Reset all state
    this.reset();
  }
}

// Export singleton instance
export const diallerState = new DiallerState();
